import { useCallback, useEffect, useMemo, useState } from 'react';
import { reportViewApi } from '@/api/reportView.api';
import type { ReportViewDto, SaveReportViewDto } from '@/types/reportView.types';

export interface SavedView extends ReportViewDto {
  /** filtersJson parsed to an object, or null when absent/invalid */
  parsedFilters: unknown;
}

export interface SaveViewOptions {
  shared?: boolean;
  isDefault?: boolean;
}

function parseFilters(filtersJson: string | null): unknown {
  if (!filtersJson) return null;
  try {
    return JSON.parse(filtersJson) as unknown;
  } catch {
    return null;
  }
}

/**
 * Saved report views for one report key.
 * Serialises filters with JSON.stringify on save; parses filtersJson
 * into `parsedFilters` per view on load.
 */
export const useSavedViews = (reportKey: string) => {
  const [views, setViews] = useState<SavedView[]>([]);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reportViewApi.getViews(reportKey);
      const list = res.data?.success ? res.data.data ?? [] : [];
      setViews(list.map(v => ({ ...v, parsedFilters: parseFilters(v.filtersJson) })));
    } catch {
      setViews([]);
    } finally {
      setLoading(false);
    }
  }, [reportKey]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const buildDto = useCallback(
    (name: string, filters: unknown, opts?: SaveViewOptions): SaveReportViewDto => ({
      reportKey,
      name,
      filtersJson: filters === undefined || filters === null ? null : JSON.stringify(filters),
      columnsJson: null,
      isShared: opts?.shared ?? false,
      isDefault: opts?.isDefault ?? false,
    }),
    [reportKey],
  );

  const save = useCallback(
    async (name: string, filters: unknown, opts?: SaveViewOptions) => {
      const dto = buildDto(name, filters, opts);
      if (dto.isShared) {
        await reportViewApi.createShared(dto);
      } else {
        await reportViewApi.create(dto);
      }
      await reload();
    },
    [buildDto, reload],
  );

  const update = useCallback(
    async (id: string, name: string, filters: unknown, opts?: SaveViewOptions) => {
      const dto = buildDto(name, filters, opts);
      if (dto.isShared) {
        await reportViewApi.updateShared(id, dto);
      } else {
        await reportViewApi.update(id, dto);
      }
      await reload();
    },
    [buildDto, reload],
  );

  const remove = useCallback(
    async (id: string) => {
      await reportViewApi.remove(id);
      await reload();
    },
    [reload],
  );

  const defaultView = useMemo(
    () => views.find(v => v.isDefault && v.isOwn) ?? views.find(v => v.isDefault) ?? null,
    [views],
  );

  return { views, loading, reload, save, update, remove, defaultView };
};
